import {pick} from '@benchling/js-utils';
import assert from 'assert';

import {
  addAndUpdateSteps,
  deleteAnalysisSteps,
  addStepToAnalysisWithInferredEdges,
} from 'analysis-tool/AnalysisActions';
import {VIEW_END_STEP_TYPES} from 'analysis-tool/data/LineageStepTypes';
import {getStepIdFromFilterId} from 'analysis-tool/filters/GetDataFrameFilterOptions';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {DataFrameFilterOptions} from 'data-frames/filters/DataFrameFilterOptions';
import {
  DataFrameFilter,
  DataFrameFilterOperation,
  DataFrameFilterStepConfig,
} from 'data-frames/filters/DataFrameFilterTypes';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {
  PipelineGraphQL,
  PipelineStep,
  PipelineStepData,
  PipelineViewGraphQL,
  pipelineStepIsStepType,
} from 'pipelines/PipelineTypes';
import {resolveLineageStep, resolveOutputStepData} from 'pipelines/PipelineViewUtils';
import {getOutputStepDataRawDatas, getPipelineStepRawDatasOfKind} from 'pipelines/StepDataUtils';
import {withLoadingScreen} from 'util/Promises';
import {JSONObject} from 'util/Types';

/** `DataFrameFilterOptions` to use if steps are running or failed. */
const DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP: DataFrameFilterOptions = {
  getFilterForColumnHeaders: () => null,
  getFilterForColumn: () => null,
  // oxlint-disable-next-line require-await
  updateFilterForColumn: async () => undefined,
  dataFrameSpec: {columns: [], name: 'Unknown'},
  getFiltersWithMetadata: () => [],
  getUnfilteredColumns: () => [],
};

/**
 * Get the existing filter step to modify, if any.
 *
 * If the last step of a view – excluding any TABLE_VIEW, CHART_VIEW or REGRESSION_VIEW steps – is a filter
 * step, then new filters should be added onto that FILTER_DATASET step. Otherwise, a new FILTER_DATASET step
 * should be created.
 */
function getExistingFilterStep(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>
): PipelineStep<PipelineStepType.FILTER_DATASET, 0> | null {
  for (const lineageNode of view.lineage.toReversed()) {
    const step = resolveLineageStep(steps, lineageNode);
    if (pipelineStepIsStepType(PipelineStepType.FILTER_DATASET, 0, step)) {
      return step;
    }
    if (VIEW_END_STEP_TYPES.includes(step.stepType)) {
      continue;
    }
    return null;
  }
  return null;
}

/** Find a filter by its id; a filterId may reference any filter step in the view. */
function getFilterStepByFilterId(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>,
  filterId: string
): PipelineStep<PipelineStepType.FILTER_DATASET, 0> | null {
  const node = view.lineage.find((node) => node.step.id === getStepIdFromFilterId(filterId));
  if (node == null) {
    return null;
  }
  const step = resolveLineageStep(steps, node);
  return pipelineStepIsStepType(PipelineStepType.FILTER_DATASET, 0, step) ? step : null;
}

/**
 * Get the step data to connect a new filter step to.
 *
 * This is either the final output step data of the view or – if the final step is a TABLE_VIEW, CHART_VIEW or
 * REGRESSION step – the input step data of the final step.
 */
function getStepDataToConnectNewFilterStep(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>
): {
  stepData: PipelineStepData;
  stepDataIndex: number | null;
} | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  const step = resolveLineageStep(steps, lastNode);

  if (VIEW_END_STEP_TYPES.includes(step.stepType)) {
    return {stepData: step.inputStepDatas[0], stepDataIndex: 0};
  } else if (lastNode.stepOutputReference != null) {
    const outputStepData = resolveOutputStepData(steps, {
      step: lastNode.step,
      stepOutputReference: lastNode.stepOutputReference,
    });
    return {stepData: outputStepData, stepDataIndex: lastNode.stepOutputReference.rawDataIndex};
  }
  return null;
}

/** Get the data frame spec on which a new filter would be applied. */
function getCurrentDataFrameSpec(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>
): DataFrameSpec {
  const emptySpec = DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP.dataFrameSpec;

  // If there is an existing filter step, the current data frame spec is the input to the filter step.
  const existingFilterStep = getExistingFilterStep(view, steps);
  if (existingFilterStep) {
    const dataFrames = getOutputStepDataRawDatas(existingFilterStep, 'Output dataset');
    return dataFrames.length === 1 ? pick(dataFrames[0], 'name', 'columns') : emptySpec;
  }

  // Otherwise, the current data frame spec comes from whichever step data to which a new filter step would be
  // connected.
  const stepDataInfo = getStepDataToConnectNewFilterStep(view, steps);
  if (stepDataInfo) {
    const dataFrames = getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, stepDataInfo.stepData);
    return dataFrames.length > (stepDataInfo.stepDataIndex ?? 0)
      ? pick(dataFrames[stepDataInfo.stepDataIndex ?? 0], 'name', 'columns')
      : emptySpec;
  }

  return emptySpec;
}

/** Get filters for all columns, from a given step. */
function getAllFiltersFromFilterStep(
  filterStep: PipelineStep<PipelineStepType.FILTER_DATASET, 0> | null
): Array<DataFrameFilter> {
  const config = filterStep?.config as DataFrameFilterStepConfig | null;
  return config?.filter?.filters ?? [];
}

/** Check if a filter is for a particular column. */
function filterMatchesColumn(filter: DataFrameFilter, columnSystemName: string): boolean {
  return filter.filters.every((filterCondition) => filterCondition.columnSystemName === columnSystemName);
}

/** Get the filter for a specific column on a specific step. */
function getFilterForColumnSystemName(
  filterStep: PipelineStep<PipelineStepType.FILTER_DATASET, 0> | null,
  columnSystemName: string
): DataFrameFilter | null {
  return (
    getAllFiltersFromFilterStep(filterStep).find((filter) => filterMatchesColumn(filter, columnSystemName)) ??
    null
  );
}

/** Get columns which are not filtered on the current filter step.  */
function getUnfilteredColumns(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>
): DataFrameSpec['columns'] {
  return getCurrentDataFrameSpec(view, steps).columns.filter(
    ({systemName}) => getFilterForColumnSystemName(getExistingFilterStep(view, steps), systemName) == null
  );
}

/** Update or remove a FILTER_DATASET step. The step will be removed if there are no more filters. */
async function updateOrRemoveFilterStep(
  analysis: PipelineGraphQL,
  clientStateId: string,
  step: PipelineStep<PipelineStepType.FILTER_DATASET, 0>,
  columnSystemName: string,
  filter: DataFrameFilter | null
): Promise<void> {
  const filtersToKeep = getAllFiltersFromFilterStep(step).filter(
    (filter) => !filterMatchesColumn(filter, columnSystemName)
  );
  const updatedFilters = filter == null ? filtersToKeep : [...filtersToKeep, filter];

  if (updatedFilters.length === 0) {
    await withLoadingScreen(
      deleteAnalysisSteps(
        analysis,
        {stepIds: [step.id], behavior: DeletePipelineStepsBehavior.NO_INFERENCE_WITH_REPARENTING},
        clientStateId
      )
    );
  } else {
    await withLoadingScreen(
      addAndUpdateSteps(analysis.id, {
        stepsToAdd: [],
        stepsToUpdate: [
          {
            stepId: step.id,
            config: {
              filter: {operation: DataFrameFilterOperation.AND, filters: updatedFilters},
            } as unknown as Record<string, JSONObject>,
            rerunAfterUpdate: true,
          },
        ],
      })
    );
  }
}

/** Add a FILTER_DATASET step. */
async function addFilterStep(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  filter: DataFrameFilter | null
): Promise<void> {
  const stepDataToConnect = getStepDataToConnectNewFilterStep(view, analysis.steps);

  if (stepDataToConnect == null) {
    notifications.error('A filter step cannot be added here.');
    return;
  }

  const config = {filter: {operation: DataFrameFilterOperation.AND, filters: [filter]}} as Record<
    string,
    JSONObject
  >;

  await withLoadingScreen(
    addStepToAnalysisWithInferredEdges(analysis, {
      stepType: PipelineStepType.FILTER_DATASET,
      stepData: stepDataToConnect.stepData,
      stepDataIndex: stepDataToConnect.stepDataIndex,
      config,
      runAfterCreate: true,
    })
  );
}

/** Update a filter for a column. This may add, update or remove a FILTER_DATASET step. */
async function updateFilterForColumn(
  analysis: PipelineGraphQL,
  clientStateId: string,
  view: PipelineViewGraphQL,
  columnSystemName: string,
  filter: DataFrameFilter | null,
  filterId?: string
): Promise<void> {
  const step =
    filterId == null
      ? getExistingFilterStep(view, analysis.steps)
      : getFilterStepByFilterId(view, analysis.steps, filterId);
  if (step) {
    await updateOrRemoveFilterStep(analysis, clientStateId, step, columnSystemName, filter);
  } else {
    await addFilterStep(analysis, view, filter);
  }
}

/**
 * Make functions to render and update a filters on data frames.
 *
 * A `FILTER_DATASET` step can contain filters for more than one column, and each column's filter can contain
 * more than one condition.
 *
 * The `DataFrameFilterOptions` interface allows updating the filter for a specific columns. This allows
 * allows column headers to show column-specific filters, even if the underlying `FILTER_DATASET` step is
 * more complex.
 *
 * @param analysis The analysis containing a data frame that may be filtered
 * @param clientStateId Client state ID of the analysis
 * @param view View being rendered
 */
export default function getViewBasedDataFrameFilterOptions(
  analysis: PipelineGraphQL,
  clientStateId: string,
  view: PipelineViewGraphQL
): DataFrameFilterOptions {
  // Disable filter options if any step in the view, except the filter step or its child, is not SUCCEEDED.
  const existingFilterStep = getExistingFilterStep(view, analysis.steps);
  if (
    view.lineage.some((lineageNode) => {
      const step = resolveLineageStep(analysis.steps, lineageNode);
      return (
        step.statusCode !== PipelineStepStatusCode.SUCCEEDED &&
        step.id !== existingFilterStep?.id &&
        !step.incomingEdges.some(({fromStep}) => fromStep.id === existingFilterStep?.id)
      );
    })
  ) {
    return DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP;
  }

  return {
    getFilterForColumnHeaders: (columnSystemName) =>
      getFilterForColumnSystemName(existingFilterStep, columnSystemName),
    getFilterForColumn: (columnSystemName, filterId) =>
      getFilterForColumnSystemName(getFilterStepByFilterId(view, analysis.steps, filterId), columnSystemName),
    updateFilterForColumn: (columnSystemName, filter, filterId) =>
      updateFilterForColumn(analysis, clientStateId, view, columnSystemName, filter, filterId),
    dataFrameSpec: getCurrentDataFrameSpec(view, analysis.steps),
    getUnfilteredColumns: () => getUnfilteredColumns(view, analysis.steps),
    /** @deprecated This option is not supported in views-based code. */
    getFiltersWithMetadata: () => {
      assert(false, 'Used getFiltersWithMetadata with ENABLE_INSIGHTS_LABS');
    },
  };
}
